import type { LineBufferGeometryCache } from '@gpx-spa/geo-adapters';
import type { LngLatCoord, RoutePlan } from '@gpx-spa/geo-domain';
import {
  buildShipmentDeviceApiPayload,
  buildShipmentRouteSegmentsFromResolvedStops,
  type ShipmentStopIdRow,
} from '@/utils/device-api-shipment-payload';
import {
  collectPayloadGeometryMapsForRoutePlan,
  ensureCorridorBuffersForRoutePlan,
} from '@/components/shipment/form/domain/corridor-geometry';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { isValidPlannedDate } from '@/components/shipment/form/utils/shipment-form-stop-validation';

function notificationEmailOrPhoneListFromForm(raw: unknown): string[] {
  if (raw == null || !Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (item == null) return '';
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null && 'value' in item) {
        return String((item as { value: string }).value ?? '');
      }
      return String(item);
    })
    .filter((s) => s.length > 0);
}

function notifyAtFromForm(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const v = (raw as { value: unknown }).value;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v !== '') {
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  }
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string' && raw !== '') {
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/**
 * Normalizes Notifications block values to the same shape as legacy `POST /shipments` (scalar + string arrays).
 */
export function normalizeShipmentFormNotificationFields(input: {
  notifyAt?: unknown;
  notificationEmails?: unknown;
  notificationPhones?: unknown;
}): {
  notifyAt: number | null;
  notificationEmails: string[];
  notificationPhoneNumbers: string[];
} {
  return {
    notifyAt: notifyAtFromForm(input.notifyAt),
    notificationEmails: notificationEmailOrPhoneListFromForm(
      input.notificationEmails,
    ),
    notificationPhoneNumbers: notificationEmailOrPhoneListFromForm(
      input.notificationPhones,
    ),
  };
}

function plannedAtToIso(d: Date | null): string | null {
  if (d == null || !isValidPlannedDate(d)) return null;
  return d.toISOString();
}

function sortKeysDeep(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(sortKeysDeep);
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    out[k] = sortKeysDeep(obj[k]);
  }
  return out;
}

export type ShipmentFormStopsToDeviceApiOptions = {
  /** When false, omit `id` even if `persistedStopId` is set (POST create). Default true. */
  includeIds?: boolean;
};

/**
 * Maps Pinia stop rows to gpx-device-api nested `stops` (circle | geofence).
 */
export function shipmentFormStopsToDeviceApiStops(
  rows: ShipmentFormStopRow[],
  options?: ShipmentFormStopsToDeviceApiOptions,
): Record<string, unknown>[] {
  const includeIds = options?.includeIds !== false;
  return rows.map((row, sequence) => {
    const times = {
      planned_departure_at: plannedAtToIso(row.plannedDeparture),
      planned_arrival_at: plannedAtToIso(row.plannedArrival),
    };
    const id =
      includeIds &&
      typeof row.persistedStopId === 'number' &&
      row.persistedStopId > 0
        ? row.persistedStopId
        : undefined;
    if (row.geofence) {
      return {
        ...(id != null ? { id } : {}),
        sequence,
        type: 'geofence',
        name: row.geofence.name ?? row.resolved?.formattedAddress ?? null,
        geofence_id: row.geofence.id,
        ...times,
      };
    }
    const place = row.resolved;
    if (!place?.coordinates) {
      throw new Error(`Stop ${sequence + 1} is missing coordinates.`);
    }
    return {
      ...(id != null ? { id } : {}),
      sequence,
      type: 'circle',
      name: place.formattedAddress,
      center: { lat: place.coordinates.lat, lng: place.coordinates.lng },
      radius: Math.round(row.radiusM),
      ...times,
    };
  });
}

/**
 * Canonical JSON for comparing “same stops payload” before POST/PATCH (order-insensitive keys per object).
 */
export function stableStringifyShipmentStopsDevicePayload(
  rows: ShipmentFormStopRow[],
): string {
  const payload = shipmentFormStopsToDeviceApiStops(rows);
  return JSON.stringify(sortKeysDeep(payload));
}

export type ShipmentFormV2Result = {
  /** POST /api/v2/accounts/{account}/shipments (stops only; no routes). */
  shipmentPayload: Record<string, unknown>;
  /** One segment per POST …/shipments/{id}/routes — call with `stops` from the create response (id + sequence). */
  buildRouteSegments: (
    stopsFromApi: ShipmentStopIdRow[],
  ) => Record<string, unknown>[];
};

/**
 * Device-API–style nested payload (preview / future POST), aligned with
 * {@link buildShipmentFormDeviceApiPreviewJson} in prototypes.
 */
export async function buildShipmentFormDeviceApiPayload(input: {
  shipmentId: number;
  accountId: number;
  deviceIds: number[];
  name: string;
  carrier?: string | null;
  notifyAt?: unknown;
  notificationEmails?: unknown;
  notificationPhones?: unknown;
  stops: ShipmentFormStopRow[];
  routePlan: RoutePlan | null;
  geometryQualityPercent: number;
  corridorBufferM: number;
  selectedByPathId: Record<string, boolean>;
  geoCache: LineBufferGeometryCache;
}): Promise<ShipmentFormV2Result> {
  const stopsPayload = shipmentFormStopsToDeviceApiStops(input.stops);
  const { notifyAt, notificationEmails, notificationPhoneNumbers } =
    normalizeShipmentFormNotificationFields({
      notifyAt: input.notifyAt,
      notificationEmails: input.notificationEmails,
      notificationPhones: input.notificationPhones,
    });

  if (!input.routePlan?.segments?.length) {
    const payloadInput = {
      shipmentId: input.shipmentId,
      accountId: input.accountId,
      deviceIds: input.deviceIds,
      name: input.name,
      carrier: input.carrier ?? null,
      notifyAt,
      notificationEmails,
      notificationPhoneNumbers,
      stops: stopsPayload,
      routePlan: { segments: [] },
      selectedByPathId: {},
      corridorWidthMeters: input.corridorBufferM,
      pathCoordinatesById: {},
    };
    return {
      shipmentPayload: buildShipmentDeviceApiPayload(
        payloadInput,
        'engineV2Create',
      ),
      buildRouteSegments: () => [],
    };
  }

  await ensureCorridorBuffersForRoutePlan(
    input.routePlan,
    input.selectedByPathId,
    input.geoCache,
    input.corridorBufferM,
    input.geometryQualityPercent,
  );

  const { pathCoordinatesById } = collectPayloadGeometryMapsForRoutePlan(
    input.routePlan,
    input.selectedByPathId,
    input.geometryQualityPercent,
    input.corridorBufferM,
    input.geoCache,
  );

  const payloadInput = {
    shipmentId: input.shipmentId,
    accountId: input.accountId,
    deviceIds: input.deviceIds,
    name: input.name,
    carrier: input.carrier ?? null,
    notifyAt,
    notificationEmails,
    notificationPhoneNumbers,
    stops: stopsPayload,
    routePlan: input.routePlan,
    selectedByPathId: input.selectedByPathId,
    corridorWidthMeters: input.corridorBufferM,
    pathCoordinatesById: pathCoordinatesById as Record<string, LngLatCoord[]>,
  };

  return {
    shipmentPayload: buildShipmentDeviceApiPayload(
      payloadInput,
      'engineV2Create',
    ),
    buildRouteSegments: (stopsFromApi: ShipmentStopIdRow[]) =>
      buildShipmentRouteSegmentsFromResolvedStops(payloadInput, stopsFromApi),
  };
}
